import { Option, OptionGroup } from '@fluentui/react-components';
import type { IField, ILookupValue, IMetadata } from '@moko/utils';
import { getMDL2Icon } from './utils';

interface LookupOptionsProps {
  metadata: IMetadata;
  field: IField;
}

// Group consecutive non-header values under the preceding header entry.
// Values before the first header are placed in an ungrouped section (label: null).
function groupValues(
  values: ILookupValue[]
): { label: string | null; items: ILookupValue[] }[] {
  const groups: { label: string | null; items: ILookupValue[] }[] = [];
  let current: { label: string | null; items: ILookupValue[] } | null = null;

  for (const v of values) {
    if (v.header) {
      current = { label: v.value, items: [] };
      groups.push(current);
    } else {
      if (!current) {
        current = { label: null, items: [] };
        groups.push(current);
      }
      current.items.push(v);
    }
  }

  return groups;
}

export const LookupOptions = ({ metadata, field }: LookupOptionsProps) => {
  const lookup = metadata.lookups.find((l) => l.name === field.lookupList);
  if (!lookup) return null;

  const hasAnyIcon = lookup.values.some((v) => getMDL2Icon(v.icon));
  const hasHeaders = lookup.values.some((v) => v.header);

  const renderOption = (v: ILookupValue) => {
    const IconComponent = getMDL2Icon(v.icon);
    return (
      <Option key={`${field.name}_${v.key}`} value={v.key} text={v.value}>
        {hasAnyIcon &&
          (IconComponent ? (
            <IconComponent style={{ fontSize: 16, marginRight: 8 }} />
          ) : (
            <span style={{ width: 24, marginRight: 8 }} />
          ))}
        {v.optionLabel ?? v.value}
      </Option>
    );
  };

  if (!hasHeaders) {
    return <>{lookup.values.map(renderOption)}</>;
  }

  const groups = groupValues(lookup.values);

  return (
    <>
      {groups.map((group, i) =>
        group.label !== null ? (
          <OptionGroup key={`${field.name}_group_${i}`} label={group.label}>
            {group.items.map(renderOption)}
          </OptionGroup>
        ) : (
          group.items.map(renderOption)
        )
      )}
    </>
  );
};
